import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { documentTitleForPath, getRouteMetadata } from '../lib/routeMetadata.js';

/**
 * Keeps SPA navigation equivalent to a document navigation for keyboard and
 * assistive-technology users. Query-string changes intentionally do not move
 * focus, so filtering and transaction detail overlays retain their context.
 */
export function RouteAccessibility() {
  const { pathname } = useLocation();
  const previousPathname = useRef<string | null>(null);
  const [announcement, setAnnouncement] = useState(() => getRouteMetadata(pathname).announcement);

  useEffect(() => {
    const metadata = getRouteMetadata(pathname);
    document.title = documentTitleForPath(pathname);

    const isClientNavigation = previousPathname.current !== null
      && previousPathname.current !== pathname;
    previousPathname.current = pathname;

    setAnnouncement('');
    const timer = window.setTimeout(() => {
      setAnnouncement(metadata.announcement);
      if (!isClientNavigation) return;

      const main = document.getElementById('main-content');
      if (!(main instanceof HTMLElement)) return;
      if (typeof main.scrollTo === 'function') main.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      main.focus({ preventScroll: true });
    }, 0);

    return () => window.clearTimeout(timer);
  }, [pathname]);

  return (
    <span className="sr-only" aria-live="polite" aria-atomic="true" data-route-announcer>
      {announcement}
    </span>
  );
}
